import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BusinessService } from '../../services';
import type { NucleusServiceResponse } from '../../services/types';
import { handleNucleusResponse } from '../../services/utils';
import type {
  Business,
  BusinessSettings,
  UpdateBusinessRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';

import { useCurrentUser } from '../users/useCurrentUser';
import {
  BASE_BUSINESSES_QUERY_KEY,
  useAccessibleBusinesses,
} from './useAccessibleBusinesses';

export const useCurrentBusiness = () => {
  const { currentUser, isLoading: userLoading } = useCurrentUser();
  const { accessibleBusinesses, isLoading: isAccessibleBusinessesLoading } =
    useAccessibleBusinesses();
  const businessId =
    currentUser?.preferences?.selectedBusinessId ??
    accessibleBusinesses?.[0]?.id; // fallback to first accessible business if no business is selected

  const queryClient = useQueryClient();

  const QUERY_KEY = ['currentBusiness', businessId];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!businessId) {
        throw new Error('No business selected');
      }
      const response = await BusinessService.fetchBusinessById(businessId);

      const data = handleNucleusResponse(response).data;
      if (response.success) {
        getTokenManager().setBusinessId(response.data.id);
      }

      return data;
    },
    enabled: !!businessId && !userLoading && !isAccessibleBusinessesLoading,
  });

  const updateBusiness = useMutation({
    mutationFn: (data: UpdateBusinessRequest) => {
      if (!businessId) {
        throw new Error('No business selected');
      }
      return BusinessService.updateBusiness(businessId, data);
    },
    onSuccess: (response: NucleusServiceResponse<Business>) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
        queryClient.invalidateQueries({
          queryKey: [BASE_BUSINESSES_QUERY_KEY],
        });
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateBusinessSettings = useMutation({
    mutationFn: ({
      key,
      value,
    }: {
      key: keyof NonNullable<BusinessSettings>;
      value: NonNullable<BusinessSettings>[keyof NonNullable<BusinessSettings>];
    }) => {
      if (!businessId) {
        throw new Error('No business selected');
      }
      return BusinessService.updateBusinessSettings(businessId, key, value);
    },
    onSuccess: (response: NucleusServiceResponse<Business>) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
        queryClient.invalidateQueries({
          queryKey: [BASE_BUSINESSES_QUERY_KEY],
        });
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: currentBusiness, isLoading, ...restQuery } = query;
  return {
    currentBusiness,
    isLoading: isLoading || userLoading || isAccessibleBusinessesLoading,
    updateBusiness,
    updateBusinessSettings,
    ...restQuery,
  };
};
